import React, {createContext, useContext, useState, useEffect, ReactNode} from 'react';
import {supabase} from '@/integrations/supabase/client';
import {toast} from '@/hooks/use-toast';
import {Session} from '@supabase/supabase-js';
import {User} from "@/features/user";

// Define the team role type to match our new enum in the database
export type TeamRole = 'superadmin' | 'admin' | 'member' | 'guest';

type AuthContextType = {
    user: User | null;
    session: Session | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<void>;
    signup: (email: string, password: string, name?: string) => Promise<void>;
    logout: () => Promise<void>;
    resetPassword: (email: string) => Promise<void>;
    getUserRole: (userId: string, teamId: string) => Promise<TeamRole | null>;
    isTeamAdmin: (userId: string, teamId: string) => Promise<boolean>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export const AuthProvider = ({children}: AuthProviderProps) => {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        // Set up auth state listener FIRST
        const {data: authListener} = supabase.auth.onAuthStateChange(
            async (event, session) => {
                setSession(session);
                setUser(await getUser(session.user) ?? null);
                setIsLoading(false);
            }
        );

        // THEN check for existing session
        const checkSession = async () => {
            const {data} = await supabase.auth.getSession();
            setSession(data.session);
            setUser(await getUser(data.session?.user) ?? null);
            setIsLoading(false);
        };

        checkSession().catch(console.error);

        return () => {
            authListener?.subscription.unsubscribe();
        };
    }, []);

    const login = async (email: string, password: string) => {
        setIsLoading(true);
        try {
            const {error} = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            toast({
                title: "Login successful",
                description: "Welcome back!",
            });
        } catch (error) {
            console.error('Login error:', error);
            toast({
                title: "Login failed",
                description: error.message || "Please check your credentials and try again.",
                variant: "destructive",
            });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const signup = async (email: string, password: string, name?: string) => {
        setIsLoading(true);
        try {
            const {error} = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: name,
                    },
                },
            });

            if (error) throw error;

            toast({
                title: "Account created",
                description: "Welcome to Drimt!",
            });
        } catch (error) {
            console.error('Signup error:', error);
            toast({
                title: "Signup failed",
                description: error.message || "There was an error creating your account.",
                variant: "destructive",
            });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = async () => {
        setIsLoading(true);
        try {
            const {error} = await supabase.auth.signOut();

            if (error) throw error;

            toast({
                title: "Logged out",
                description: "You have been successfully logged out.",
            });
        } catch (error) {
            console.error('Logout error:', error);
            toast({
                variant: "destructive",
                title: "Logout failed",
                description: error.message || "An error occurred during logout.",
            });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };

    const resetPassword = async (email: string) => {
        setIsLoading(true);
        try {
            const {error} = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) throw error;

            toast({
                title: "Password reset email sent",
                description: "Check your inbox for instructions.",
            });
        } catch (error) {
            console.error('Reset password error:', error);
            toast({
                variant: "destructive",
                title: "Failed to send reset email",
                description: error.message || "Please try again later.",
            });
            throw error;
        } finally {
            setIsLoading(false);
        }
    };


    /**
     * Retrieves user information from the 'profiles' table and additional details
     * such as teams associated with the user by invoking a remote procedure.
     *
     * @param {Session['user']} authUser - The authenticated user's information from the session.
     * @returns {Promise<User | null>} A promise that resolves to the combined user data or null if an error occurs.
     */
    const getUser = async (authUser: Session['user']):Promise<User | null> => {
        try {
            return {
                avatar: "", name: "",
                ...authUser,
                projects: [],
                navMainItems: [],
                email: authUser.email || '',
                teams: []
            }
        } catch (error) {
            console.error('Error getting user role:', error);
            return null;
        }
    };

    // New function to get the user's role in a team
    const getUserRole = async (userId: string, teamId: string): Promise<TeamRole | null> => {

        try {
            // Use the new database function we created
            const {data, error} = await supabase
                .rpc('get_team_member_role', {
                    team_id: teamId,
                    user_id: userId
                });

            if (error) {
                console.error('Error getting user role:', error);
                return null;
            }

            return data as TeamRole;
        } catch (error) {
            console.error('Error getting user role:', error);
            return null;
        }
    };

    // Helper function to check if the user is a team admin or superadmin
    const isTeamAdmin = async (userId, teamId: string): Promise<boolean> => {
        const role = await getUserRole(userId, teamId);
        return role === 'admin' || role === 'superadmin';
    };

    return (
        <AuthContext.Provider value={{
            user,
            session,
            isLoading,
            login,
            signup,
            logout,
            resetPassword,
            getUserRole,
            isTeamAdmin
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
